import { Injectable } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import {
  BaseFileExtension,
  BaseMimeType,
  FallbackMimeType,
  FALLBACK_MIME,
} from '../enums/base-available-file-type.enum';
import { EXTENSION_TO_MIME } from '../constants/base-file-types';

export interface FileDownloadResponse {
  body: Blob | ArrayBuffer;
  fileName: string;
}

export interface DownloadOptions {
  isPreview?: boolean;
  documentType?: BaseFileExtension | BaseMimeType | string;
  mimeType?: BaseMimeType | string;
}

export type DownloadResult = Blob | null;

@Injectable({ providedIn: 'root' })
export class BaseFileDownloadService {
  downloadFile(source$: Observable<FileDownloadResponse>, options: DownloadOptions = {}): Observable<DownloadResult> {
    const { isPreview = false, documentType, mimeType } = options;

    if (isPreview) {
      return source$.pipe(map((response) => this.ensureBlob(response.body, documentType, mimeType)));
    }

    return source$.pipe(
      tap((response) => this.triggerBrowserDownload(response.body, response.fileName, documentType, mimeType)),
      map(() => null)
    );
  }

  triggerBrowserDownload(
    fileData: Blob | ArrayBuffer,
    fileName: string,
    documentType?: BaseFileExtension | BaseMimeType | string,
    customMimeType?: BaseMimeType | string
  ): void {
    const blob = this.ensureBlob(fileData, documentType, customMimeType);
    const objectUrl = URL.createObjectURL(blob);

    try {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = (fileName ?? 'download').trim();
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    }
  }

  private ensureBlob(
    fileData: Blob | ArrayBuffer,
    documentType?: BaseFileExtension | BaseMimeType | string,
    customMimeType?: BaseMimeType | string
  ): Blob {
    const mimeType = String(customMimeType ?? this.getMimeType(documentType));

    if (fileData instanceof Blob) {
      return fileData.type === mimeType ? fileData : fileData.slice(0, fileData.size, mimeType);
    }

    return new Blob([fileData], { type: mimeType });
  }

  private getMimeType(documentType?: BaseFileExtension | BaseMimeType | string): BaseMimeType | FallbackMimeType {
    if (!documentType) {
      return BaseMimeType.pdf;
    }

    const normalized = String(documentType).trim().toLowerCase();

    if (normalized.includes('/')) {
      return normalized as BaseMimeType;
    }

    const asExtension = normalized as BaseFileExtension;
    return EXTENSION_TO_MIME[asExtension] ?? FALLBACK_MIME;
  }
}
